import React from 'react';
import { Upload, message } from 'antd';
import type { UploadProps } from 'antd/es/upload/interface';
import { RcFile } from 'antd/es/upload';
import { FolderAdd } from 'iconsax-react';
import { useSearchParams } from 'react-router-dom';

import { UploadedFile } from '../../../../types/chat';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../../../store/slices/chat';
import { useUploadChatDocumentsMutation } from '../../../../store/services/chatService';

interface FileUploadInputProps {
  onFilesChange: (
    files: UploadedFile[] | ((prev: UploadedFile[]) => UploadedFile[])
  ) => void;
  onFileIdsChange: (id: string) => void;
  isUploading: boolean;
  onUploadingChange: (isUploading: boolean) => void;
  onRemove: (uid: string) => void;
  setInputLoading: Dispatch<SetStateAction<boolean>>;
  roomId?: string;
}

const FileUploadInput = ({
  onFilesChange,
  onFileIdsChange,
  onUploadingChange,
  setInputLoading,
  roomId: propRoomId,
}: FileUploadInputProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { selectedRoomId } = useChat();
  const effectiveRoomId =
    propRoomId || selectedRoomId || searchParams.get('chatRoomId') || undefined;

  const [uploadChatDocuments] = useUploadChatDocumentsMutation();

  const beforeUploadFile = (file: RcFile) => {
    const isLt5M = file.size / 1024 / 1024 < 5; // 5MB limit
    if (!isLt5M) {
      message.error(t('file_size_limit', { size: 5 }));
    }
    return isLt5M || Upload.LIST_IGNORE;
  };

  const uploadFile = async (file: RcFile) => {
    setInputLoading(true);
    try {
      const formData = new FormData();
      formData.append('files', file);

      const res = await uploadChatDocuments({
        formData,
        ...(effectiveRoomId && { roomId: effectiveRoomId }),
      }).unwrap();

      return res;
    } catch (error) {
      console.error('Upload failed:', error);
      message.error(t('somthingWentWrong'));
      return null;
    } finally {
      setInputLoading(false);
    }
  };

  const handleFileUpload: UploadProps['onChange'] = async (info) => {
    if (info.file.status === 'uploading') {
      onUploadingChange(true);
      onFilesChange((prev: UploadedFile[]) => {
        const existing = prev.find((file) => file.uid === info.file.uid);
        if (existing) {
          return prev.map((file) =>
            file.uid === info.file.uid
              ? { ...file, percent: info.file.percent }
              : file
          );
        }
        return [
          ...prev,
          {
            uid: info.file.uid,
            name: info.file.name,
            status: 'uploading',
            type: info.file.type || 'application/octet-stream',
            size: info.file.size || 0,
            percent: 0,
          },
        ];
      });
    } else if (info.file.status === 'done') {
      try {
        const res = await uploadFile(info.file.originFileObj as RcFile);

        if (res?.payload?.[0]?.id) {
          onFileIdsChange(res?.payload[0]?.id);
          onFilesChange((prev: UploadedFile[]) =>
            prev.map((file) =>
              file.uid === info.file.uid
                ? { ...file, status: 'done', percent: 100 }
                : file
            )
          );
          message.success(`${info.file.name} uploaded successfully`);
        } else {
          onFilesChange((prev: UploadedFile[]) =>
            prev.filter((file) => file.uid !== info.file.uid)
          );
          message.error(`${info.file.name} upload failed.`);
        }
      } catch (err) {
        console.error('File upload error:', err);
        onFilesChange((prev: UploadedFile[]) =>
          prev.filter((file) => file.uid !== info.file.uid)
        );
        message.error(`${info.file.name} upload failed.`);
      } finally {
        onUploadingChange(false);
      }
    } else if (info.file.status === 'error') {
      onFilesChange((prev: UploadedFile[]) =>
        prev.filter((file) => file.uid !== info.file.uid)
      );
      onUploadingChange(false);
      message.error(`${info.file.name} upload failed.`);
    }
  };

  return (
    <Upload
      showUploadList={false}
      beforeUpload={beforeUploadFile}
      onChange={handleFileUpload}
      multiple
      customRequest={({ onSuccess }) => {
        onSuccess?.('ok');
      }}
      accept="application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip,application/x-zip-compressed,application/x-7z-compressed,application/x-rar-compressed,application/x-tar"
      data-testid="chat-file-upload-input"
    >
      <div
        className="flex gap-2 items-start hover:bg-black-100 p-2 rounded-lg cursor-pointer"
        data-testid="chat-file-upload-trigger"
      >
        <FolderAdd size={20} />
        <span data-testid="chat-file-upload-label">{t('attachFile')} (PDF,XLS,SVG,PWN)</span>
      </div>
    </Upload>
  );
};

export default FileUploadInput;

